// Axios client used by the admin dashboard to call the LivinHome API.
// Clerk supplies a short-lived session token for each request; the browser
// never stores or refreshes LivinHome-issued credentials.

import axios, { AxiosHeaders, type AxiosError } from 'axios';
import { getClerkToken } from './clerk-auth';

const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8787/api/v1').replace(/\/$/, '');

export const api = axios.create({
  baseURL: API_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  const token = await getClerkToken();
  if (token) {
    if (!(config.headers instanceof AxiosHeaders)) config.headers = new AxiosHeaders(config.headers);
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('livinhome:auth:logout'));
    }
    return Promise.reject(error);
  },
);

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> };
}

export function extractApiError(err: unknown): { code?: string; message: string } {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error) return { code: body.error.code, message: body.error.message };
    return { message: err.message };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: 'Unknown error' };
}

export function extractApiErrorDetails(err: unknown): Record<string, unknown> | undefined {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    return body?.error?.details;
  }
  return undefined;
}
